import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, ShieldAlert, Quote, CheckCircle2, XCircle, RotateCcw, Award } from "lucide-react";
import { toast } from "sonner";

const MIN_CHARS = 20;

export default function ProvaPeace({ courseId, onExit }) {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const carica = () => {
    setData(null); setAnswers({}); setResult(null);
    api.get(`/courses/${courseId}/peace/start`).then((r) => setData(r.data)).catch(() => toast.error("Prova non disponibile"));
  };
  useEffect(carica, [courseId]);

  const tuttiCompilati = data && data.domande.every((d) => (answers[d.fase] || "").trim().length >= MIN_CHARS);

  const invia = async () => {
    if (!tuttiCompilati) { toast.error(`Elabora ogni risposta (almeno ${MIN_CHARS} caratteri).`); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/courses/${courseId}/peace/submit`, {
        scenario_id: data.scenario.id, answers,
      });
      if (res.status === "retry") { toast.info(res.message); return; }
      setResult(res);
    } catch {
      toast.error("Errore nell'invio. Le tue risposte sono al sicuro, riprova.");
    } finally { setBusy(false); }
  };

  if (!data)
    return (
      <div className="min-h-screen"><Navbar />
        <div className="flex items-center justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} /></div>
      </div>
    );

  if (result)
    return (
      <div className="min-h-screen"><Navbar />
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10" data-testid="peace-result">
          <div className="text-center mb-6">
            {result.superata ? (
              <><Award className="h-12 w-12 mx-auto text-accent mb-3" strokeWidth={1.5} />
                <h1 className="font-serif text-4xl font-bold" data-testid="peace-outcome">Prova superata</h1>
                <Badge className="rounded-full bg-primary text-primary-foreground mt-2">{result.esito}</Badge></>
            ) : (
              <><RotateCcw className="h-12 w-12 mx-auto text-primary mb-3" strokeWidth={1.5} />
                <h1 className="font-serif text-4xl font-bold" data-testid="peace-outcome">Riprova con calma</h1></>
            )}
          </div>

          <div className="bg-card border border-border rounded-lg p-6 mb-5">
            <div className="flex items-center gap-2 mb-2 text-primary"><Quote className="h-5 w-5" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-widest font-semibold">Il Maestro {data.maestro}</span></div>
            <p className="leading-relaxed">{result.feedback}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
            {Object.entries(result.fasi).map(([fase, ok]) => (
              <div key={fase} data-testid={`peace-fase-${fase}`}
                className={`rounded-md border p-3 text-center text-xs ${ok ? "border-accent/40 bg-accent/5" : "border-destructive/30 bg-destructive/5"}`}>
                {ok ? <CheckCircle2 className="h-4 w-4 mx-auto text-accent mb-1" strokeWidth={1.5} />
                    : <XCircle className="h-4 w-4 mx-auto text-destructive mb-1" strokeWidth={1.5} />}
                {result.fasi_label[fase]}
              </div>
            ))}
          </div>
          {!result.superata && result.fase_da_rinforzare && (
            <p className="text-sm text-muted-foreground text-center mb-6">
              Rinforza soprattutto la fase <span className="font-semibold text-foreground">{result.fasi_label[result.fase_da_rinforzare]}</span> ({result.fasi_ok}/5 fasi soddisfatte, ne servono almeno 4).
            </p>
          )}

          <div className="flex gap-3 justify-center">
            {result.superata ? (
              <Button className="rounded-sm bg-primary hover:bg-primary/90" onClick={onExit} data-testid="peace-done-btn">Torna al corso</Button>
            ) : (
              <>
                <Button variant="outline" className="rounded-sm" onClick={onExit} data-testid="peace-exit-btn">Torna al corso</Button>
                <Button className="rounded-sm bg-primary hover:bg-primary/90" onClick={carica} data-testid="peace-retry-btn">
                  <RotateCcw className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Riprova
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen"><Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8">
        <button onClick={onExit} data-testid="peace-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Torna al corso
        </button>

        <p className="text-sm uppercase tracking-widest text-primary font-semibold mb-2">Prova del Maestro · {data.maestro}</p>
        <h1 className="font-serif text-3xl font-bold mb-1">{data.scenario.titolo}</h1>
        <p className="text-xs text-muted-foreground mb-5">Analizza il caso di {data.scenario.nome} con il metodo PEACE.</p>

        <div className="bg-card border border-border rounded-md p-5 mb-4 leading-relaxed" data-testid="peace-scenario">
          {data.scenario.testo}
        </div>

        <div className="flex items-start gap-2 bg-secondary/60 border border-border rounded-md p-3 mb-6 text-xs text-muted-foreground" data-testid="peace-disclaimer">
          <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-primary" strokeWidth={1.5} />
          <span>{data.disclaimer}</span>
        </div>

        <div className="space-y-5">
          {data.domande.map((d, i) => (
            <div key={d.fase} data-testid={`peace-q-${d.fase}`}>
              <label className="block font-medium mb-1.5">
                <span className="text-primary font-semibold">{i + 1}. {d.label}</span> — {d.domanda}
              </label>
              <Textarea
                value={answers[d.fase] || ""}
                onChange={(e) => setAnswers({ ...answers, [d.fase]: e.target.value })}
                placeholder="Scrivi la tua riflessione…"
                className="rounded-sm min-h-[90px]"
                data-testid={`peace-answer-${d.fase}`}
              />
            </div>
          ))}
        </div>

        <Button onClick={invia} disabled={busy || !tuttiCompilati}
          className="rounded-sm bg-primary hover:bg-primary/90 mt-6 w-full sm:w-auto" data-testid="peace-submit-btn">
          {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Il Maestro riflette…</> : "Sottoponi la Prova al Maestro"}
        </Button>
      </div>
    </div>
  );
}
